import Modal from "@components/Modal";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { downloadCsv } from "@utils/downloadCsv";
import { formatDateDMY } from "@utils/formatDate";
import { formatNumberToCurrency } from "@utils/formatNumber";
import { findWbsPath } from "@utils/formatWbsPath";
import { isPresentationProject } from "@utils/presentationMode";
import { isUpdateJobActive, updateJobMessage } from "@contexts/projects/updateJobs";
import { useEffect, useRef, useState } from "react";
import Select from 'react-select';

const renderRow = (label, value) => {
  return <div className="data">
    <span className="accordion-label">{label}</span>
    <span className="value">{value}</span>
  </div>
}

const getStatus = {
  PENDING: 'Pendente',
  FULLY_DELIVERED: 'Entregue',
  PARTIALLY_DELIVERED: 'Entregue parcialmente',
}

export const purchaseOrderWbsLabels = (item = {}) => {
  const legacy = String(item.wbs_code || '').split('.');
  const suffix = (value, length, fallback = '') => {
    const normalized = String(value || '');
    return normalized ? normalized.slice(-length) : fallback;
  };

  return {
    unit: suffix(item.wbs_code_id_unit, 3),
    cell: suffix(item.wbs_code_id_cell, 2, legacy[0] || ''),
    step: suffix(item.wbs_code_id_step, 3, legacy[1] || ''),
    substep: suffix(item.wbs_code_id_substep, 3, legacy[2] || ''),
    service: suffix(item.wbs_code_id_service, 3, legacy[3] || ''),
  };
};

const Item = ({ index, resource, paid }) => {
  const [open, setOpen] = useState(false)

  const getLabel = (label) => {
    switch (label) {
      case 'unit':
        return 'Unidade'
      case 'cell':
        return 'Célula'
      case 'step':
        return 'Etapa'
      case 'substep':
        return 'Subetapa'
      case 'service':
        return 'Serviço'
      default:
        break;
    }
  }

  return <div key={index} className={`accordion-item offcanvas-accordion-item curve margin-bottom-8`}>
    <div>
      <div className="flex align-center margin-bottom-8">
        <button
          className={`accordion-toggle ${open ? "active" : ""}`}
          onClick={() => setOpen(!open)}
        />
        <span className="accordion-title" title={resource.supplier_name}>{resource.contract_number} - {resource.supplier_name}</span>
      </div>
    </div>
    <div className="grid gap-16 columns-4-laptop-l margin-top-12">
      {renderRow(`Documento`, resource.document_id)}
      {renderRow('Status', resource.status)}
      {renderRow(`Data do contrato`, resource?.contract_date?.split('-').reverse().join('/'))}
      {renderRow('Valor', formatNumberToCurrency(resource.total_labor_value))}
    </div>
    {open && <div className="grid gap-16 columns-4 margin-top-12">
      {renderRow(`Tipo de contrato`, resource.contract_type)}
      {renderRow(`Descrição`, resource.description)}
      {renderRow(`Descrição completa`, resource.description_full)}
      {renderRow(`Responsável`, resource.responsible_name)}
      {renderRow(`Preço do material`, resource.material_price)}
      {renderRow(`Quantidade medida`, resource.measured_quantity)}
      {renderRow(`Valor medido`, resource.measured_value)}
      {renderRow(`Quantidade`, `${resource.quantity}${resource.unit_of_measure}`)}
      {renderRow(`Fornecedor`, resource.supplier_name)}
      {renderRow(`Id do fornecedor`, resource.supplier_id)}

    </div>}
    {open && <div className={`grid gap-16 columns-1-laptop-l margin-top-12`}>
      {Object.entries(resource.wbs_info).map(([label, value]) => {
        if (value === undefined) return null
        return renderRow(getLabel(label), value)
      })}
    </div>}
  </div>
}

const PurchaseOrderItem = ({ index, resource }) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const { getPurchaseOrders } = useProjectActions();
  const [items, setItems] = useState([])

  const getItems = async () => {
    setLoading(true)
    try {
      const response = await getPurchaseOrders('', '', '', '', resource.purchaseOrderId)
      setItems(Array.isArray(response?.res) ? response.res : [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      getItems()
    }
  }, [open])


  return <><div key={index} className={`accordion-item offcanvas-accordion-item curve margin-top-8`}>
    <div>
      <div className="flex align-center margin-bottom-8">
        <button
          className={`accordion-toggle ${open ? "active" : ""}`}
          onClick={() => setOpen(!open)}
        />
        <span className="accordion-title" title={resource.supplier_name}>{resource.formattedPurchaseOrderId}</span>
      </div>
    </div>
    <div className="grid gap-16 columns-4-laptop-l margin-top-12">
      {renderRow(`Criado por`, resource.createdBy)}
      {renderRow('Status', getStatus[resource.status] || resource.status)}
      {renderRow(`Data`, formatDateDMY(resource?.date))}
      {renderRow('Valor', formatNumberToCurrency(resource.value))}
    </div>
    {open && <>
      <div className="grid gap-16 columns-4 margin-top-12">
        {renderRow(`Id do Centro de Custo`, resource.costCenterId)}
        {renderRow(`Id do Fornecedor`, resource?.supplierId)}
        {renderRow(`Documento`, resource?.forecastDocumentId)}
        {renderRow(`Data de Envio`, resource?.sentDate?.slice(0, 10).split('-').reverse().join('/'))}
      </div>
      <div className="grid gap-16 columns-1 margin-top-12">
        {renderRow(`Notas internas`, resource?.internalNotes)}
      </div>
    </>
    }
  </div>
    {open && <div className="margin-left-12">
      {loading ? <></> : items.map(i => {
        const wbsLabels = purchaseOrderWbsLabels(i);
        return <div className="accordion-item offcanvas-accordion-item curve">
          <div>
            <div className="flex align-center">
              <span className="accordion-title" title={i.supplier_name}>{i.resource_description} - {i.notes}</span>
            </div>
          </div>
          <div key={i._id} className="grid gap-16 columns-4-laptop-l padding-8 border-top">
            {renderRow('Quantidade', i.quantity)}
            {renderRow(`Percentual apropriado`, `${i.building_percentage_appropriation ?? i["building_percentage_appropriation]"] ?? 0}%`)}
            {renderRow('Preço unitário', formatNumberToCurrency(i.unit_price))}
            {renderRow('Valor total', formatNumberToCurrency(i.total_price))}
          </div>
          <div className="grid gap-16 columns-5-laptop-l padding-8 border-top">
            {renderRow(`Unidade - ${wbsLabels.unit}`, findWbsPath('units', i.wbs_code_id_unit))}
            {renderRow(`Célula - ${wbsLabels.cell}`, findWbsPath('cells', i.wbs_code_id_cell))}
            {renderRow(`Etapa - ${wbsLabels.step}`, findWbsPath('steps', i.wbs_code_id_step))}
            {renderRow(`Subetapa - ${wbsLabels.substep}`, findWbsPath('substeps', i.wbs_code_id_substep))}
            {renderRow(`Serviço - ${wbsLabels.service}`, findWbsPath('services', i.wbs_code_id_service))}
          </div>
        </div>
      })}
    </div>}
  </>
}

const OrdersAndContracts = () => {
  const { getContracts, getPurchaseOrders, getAllBuildingItems, refreshPurchaseOrders } = useProjectActions();
  const { activeProject, updateJobsByBuilding } = useProjectContext();
  const hasActiveProject = Boolean(activeProject?.wbs_code_id);
  const presentationMode = isPresentationProject(activeProject);
  const currentUpdateJob = updateJobsByBuilding?.[activeProject?.building_id];
  const updateInProgress = isUpdateJobActive(currentUpdateJob);
  const [dateLimit, setDateLimit] = useState({
    max: 0,
    min: 0,
  });
  const [resources, setResources] = useState([]);
  const [view, setView] = useState('purchases')
  const [search, setSearch] = useState({
    search: '',
    searchFor: '',
  })
  const [documents, setDocuments] = useState([])
  const [docFilter, setDocFilter] = useState('')
  const [isFiltering, setIsFiltering] = useState(false)
  const isFilteringRef = useRef(isFiltering);
  const [modalVisible, setModalVisible] = useState(false);
  const [datesToUpdate, setDatesToUpdate] = useState({
    min: '',
    max: ''
  })
  const [updateRes, setUpdateRes] = useState('')
  const [updateOk, setUpdateOk] = useState(true)
  const [requestingUpdate, setRequestingUpdate] = useState(false)
  const [levelToGet, setLevelToGet] = useState('')
  const [levelItems, setLevelItems] = useState([])
  const [itemToFilter, setItemToFilter] = useState('')
  const [count, setCount] = useState(0)

  useEffect(() => {
    isFilteringRef.current = isFiltering;
  }, [isFiltering]);

  const countRef = useRef(0);

  const filtersRef = useRef({
    levelToGet: '',
    itemToFilter: ''
  });

  useEffect(() => {
    filtersRef.current = { levelToGet, itemToFilter };
  }, [levelToGet, itemToFilter]);


  useEffect(() => {
    setDatesToUpdate({ ...datesToUpdate, min: dateLimit.min })
  }, [dateLimit.min]);

  useEffect(() => {
    setDatesToUpdate({ ...datesToUpdate, max: dateLimit.max })
  }, [dateLimit.max]);

  const fetchPurchases = async () => {
    if (!hasActiveProject) return { res: [], uniqueDocs: [] };
    const { levelToGet, itemToFilter } = filtersRef.current;
    const res = await getPurchaseOrders(dateLimit, countRef.current, docFilter, search.search, "", levelToGet.slice(0, -1), itemToFilter);
    return res ?? { res: [], uniqueDocs: [] };
  }

  const fetchContracts = async () => {
    if (!hasActiveProject) return { res: [], uniqueDocs: [] };
    const { levelToGet, itemToFilter } = filtersRef.current;
    const res = await getContracts('building', activeProject?.wbs_code_id, 'false', dateLimit, countRef.current, docFilter, search, levelToGet.slice(0, -1), itemToFilter);
    return res ?? { res: [], uniqueDocs: [] };
  }

  const getItens = async () => {
    if (!hasActiveProject) return;
    const response = view === "contracts"
      ? await fetchContracts()
      : await fetchPurchases();
    const nextResources = Array.isArray(response?.res) ? response.res : [];
    if (nextResources.length > 0) countRef.current += 50;

    setResources((prev) => [...prev, ...nextResources]);
    setDocuments(Array.isArray(response?.uniqueDocs) ? response.uniqueDocs : [])
  };

  const handleScroll = () => {
    if (isFilteringRef.current || search.searchFor !== "" || itemToFilter !== "") return;

    const scrolledTo = window.scrollY + window.innerHeight;
    const isReachBottom = document.body.scrollHeight === scrolledTo;

    if (isReachBottom) {
      getItens();
    }
  };

  useEffect(() => {
    countRef.current = 0;
  }, [view, dateLimit, search, itemToFilter, activeProject])

  useEffect(() => {
    if (!hasActiveProject) {
      setResources([]);
      setDocuments([]);
      return undefined;
    }
    if (dateLimit.max !== 0 || dateLimit.min !== 0 || docFilter !== '') {
      setIsFiltering(true);
    }
    setResources(() => []);
    getItens();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeProject?.wbs_code_id, view, dateLimit, count, itemToFilter]);

  useEffect(() => {
    setDatesToUpdate({
      min: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split("T")[0],
      max: new Date().toISOString().split("T")[0],
    })
  }, [])

  const handleWbsReset = () => {
    setIsFiltering(false);
    setLevelToGet('')
    setLevelItems([])
    setItemToFilter('')
    setResources(() => []);
    countRef.current = 0;
  }

  const handleDateReset = () => {
    setIsFiltering(false);
    setDateLimit({ max: 0, min: 0 });
    setResources(() => []);
    countRef.current = 0;
  }

  const handleResetSearch = () => {
    setIsFiltering(false);
    setSearch({ search: '', searchFor: '' });
    setResources(() => []);
    countRef.current = 0;
    setCount(p => p + 1)
  }

  const handleDateChange = (e) => {
    const { name, value } = e.target
    setDateLimit(prev => ({ ...prev, [name]: value }))
  }

  const handleFilter = () => {
    setIsFiltering(true);
    setResources(() => []);
    countRef.current = 0;
    getItens()
  }

  const handleModalVisibility = () => {
    setModalVisible(!modalVisible);
    setUpdateRes(!modalVisible ? updateJobMessage(currentUpdateJob) : '')
    setUpdateOk(currentUpdateJob?.status !== "failed")
  };

  const getLevelItems = async () => {
    if (!hasActiveProject || !levelToGet) return;
    const res = await getAllBuildingItems([levelToGet])
    setLevelItems(Array.isArray(res?.items) ? res.items : [])
  }

  useEffect(() => {
    if (levelToGet !== '') {
      getLevelItems()
    }
  }, [levelToGet])

  const handleUpdate = async () => {
    if (requestingUpdate || updateInProgress) return;
    setRequestingUpdate(true);
    const result = await refreshPurchaseOrders(datesToUpdate.min, datesToUpdate.max);
    setUpdateOk(Boolean(result?.ok || (result?.status === 409 && result?.body?.job)))
    setUpdateRes(
      result?.body?.message
      ?? (result?.ok
        ? "Atualização solicitada. Você pode sair desta página; enviaremos um e-mail quando terminar."
        : "Não foi possível iniciar a atualização. Tente novamente."),
    )
    setRequestingUpdate(false);
  }

  const handleEditSearch = (e) => {
    const { name, value } = e.target
    if (name === 'searchFor') {
      setIsFiltering(true);
    }
    setSearch(prev => ({ ...prev, [name]: value }))
  }



  return (
    <>
      {modalVisible && !presentationMode && (
        <Modal
          title="Atualizar insumos"
          close={handleModalVisibility}
        >
          <div style={{ alignItems: "center" }} className="date-range flex column gap-8">
            <div className="gap-4">
              <span className="label margin-right-8">Data inicial</span>
              <input
                type="date"
                onChange={(e) =>
                  setDatesToUpdate((prev) => ({
                    ...prev,
                    min: e.target.value,
                  }))
                }
                value={datesToUpdate.min}
                name="min"
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className="column gap-4">
              <span className="label margin-right-8">Data final</span>
              <input
                type="date"
                onChange={(e) =>
                  setDatesToUpdate((prev) => ({
                    ...prev,
                    max: e.target.value,
                  }))
                }
                value={datesToUpdate.max}
                name="max"
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
            <button disabled={!datesToUpdate.min || !datesToUpdate.max || requestingUpdate || updateInProgress} onClick={handleUpdate} className="button button-secondary small">{requestingUpdate || updateInProgress ? "Atualizando…" : "Atualizar"}</button>
          </div>
          <p style={{ color: updateOk ? 'green' : 'red', fontSize: '16px', fontStyle: 'italic' }}>{updateRes}</p>
        </Modal>
      )}
      <div className="flex gap-16 graph-labels">
        <h1 className={view === "purchases" ? 'active-graph' : ''} onClick={() => setView('purchases')}>Pedidos</h1>
        <h1 className={view === "contracts" ? 'active-graph' : ''} onClick={() => setView('contracts')}>Contratos</h1>
      </div>
      <div className="flex margin-top-16 margin-bottom-4 justify-space-between gap-16 timeline-filters align-center">
        <input type="text" value={search.search} name="search" onChange={handleEditSearch} placeholder="Buscar por item" />
        {search.search !== '' &&
          <>
            <select defaultValue='dis' name="searchFor" value={view === "purchases" ? 'contract_number' : search.searchFor} onChange={handleEditSearch} >
              <option disabled value='dis'>Buscar por:</option>
              {view === "contracts" && <option value='supplier_name'>Fornecedor</option>}
              <option value='contract_number'>Número</option>
            </select>
            <button disabled={search.search === "" || (search.searchFor === "" && view === "contracts")} className="button button-accent small" style={{ justifySelf: 'center' }} onClick={handleFilter}>Buscar</button>
          </>
        }
        {(search.searchFor !== "" || (view === "purchases" && search.search !== "")) && <button className="button button-error small" style={{ justifySelf: 'center' }} onClick={handleResetSearch}>Resetar</button>}
      </div>
      <div className="flex margin-top-16 margin-bottom-4 justify-space-between gap-16 timeline-filters align-center">


        <div className="flex justify-content-center align-center gap-8">
          <span>Filtrar por:</span>
          <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => {
            setLevelToGet(e.target.value)
            setItemToFilter('')
          }} value={levelToGet}>
            <option value='' disabled>Selecione</option>
            <option value='units'>Unidade Construtiva</option>
            <option value='cells'>Célula</option>
            <option value='steps'>Etapa</option>
            <option value='substeps'>Subetapa</option>
            <option value='services'>Serviço</option>
          </select>
          {/* {levelItems.length > 0 && <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => {
            setItemToFilter(e.target.value)
            setIsFiltering(true)
          }
          } value={itemToFilter} defaultChecked=''>
            <option value='' disabled>Selecione</option>
            {levelItems.map(i => {
              return <option key={i.wbs_code_id} value={i.wbs_code_id}>{i.building_unit_description || i.wbs_description}</option>
            })}
          </select>} */}
          {levelItems.length > 0 && <div style={{ minWidth: '350px', width: '100%' }}>
            <Select
              isClearable={false}
              name="filteredLevel"
              options={levelItems.map(i => ({ value: i?.wbs_code_id, label: i?.building_unit_description || i?.wbs_description }))}
              onChange={(e) => {
                setItemToFilter(e.value)
                setIsFiltering(true)
              }}
              classNamePrefix="select"
              placeholder='Buscar...'
            />
          </div>}
          {<div className="flex gap-8 align-center">
            {(itemToFilter !== '' || levelToGet !== "") && <button className="button button-error small" style={{ justifySelf: 'center' }} onClick={handleWbsReset}>Resetar</button>}
          </div>}
        </div>

        <div className="date-range">
          <span className="label">Início</span>
          <input type="date" onChange={handleDateChange} value={dateLimit.min} name="min" max={new Date().toISOString().split('T')[0]} />
          <span className="label">Fim</span>
          <input type="date" onChange={handleDateChange} value={dateLimit.max} name="max" max={new Date().toISOString().split('T')[0]} />
        </div>
        {<div className="flex gap-8 align-center">
          {(dateLimit.min !== 0 || dateLimit.max !== 0) && <button className="button button-error small" style={{ justifySelf: 'center' }} onClick={handleDateReset}>Resetar</button>}
        </div>}
        {view === "purchases" && !presentationMode && <button className="button button-secondary small" onClick={handleModalVisibility} title="Atualizar itens dos pedidos">Atualizar</button>}
      </div>

      {resources?.map((i) => (
        view === "contracts" ? <Item key={i._id} index={i._id} resource={i} paid={view === "paid"} /> : <PurchaseOrderItem key={i._id} index={i._id} resource={i} />
      ))}
    </>
  );
};

export default OrdersAndContracts;
